import { API_BASE, api, ApiError } from "@/lib/apiClient";
import { guestTabletPublicFetch } from "@/lib/guestTabletPublicFetch";
import { getOrCreateTouristPanelDeviceToken } from "@/lib/touristPanelDeviceStorage";
import { tenantSlug } from "@/lib/tenantSlug";
import type {
  TouristPanelAvailabilitySettingsUpdate,
  TouristPanelContent,
  TouristPanelDevice,
  TouristPanelDeviceStatus,
  TouristPanelDisplay,
  TouristPanelSoldOut,
  TouristPanelWorkstation,
} from "@/types/touristPanel";

const TENANT_HEADER = "X-Tenant-Slug";
export const TOURIST_PANEL_DEVICE_HEADER = "X-Tourist-Panel-Device-Token";

function publicHeaders(extra?: HeadersInit): HeadersInit {
  const token = getOrCreateTouristPanelDeviceToken();
  return {
    [TENANT_HEADER]: tenantSlug(),
    Accept: "application/json",
    [TOURIST_PANEL_DEVICE_HEADER]: token,
    ...(extra as Record<string, string> | undefined),
  };
}

function buildPublicUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function publicFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await guestTabletPublicFetch(buildPublicUrl, publicHeaders, path, init);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    const message =
      err instanceof Error &&
      (err.message === "Load failed" || err.message === "Failed to fetch")
        ? "Verbindung zum Server fehlgeschlagen."
        : err instanceof Error
          ? err.message
          : "Netzwerkfehler";
    throw new ApiError(0, message);
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { detail?: string };
      if (typeof parsed.detail === "string") message = parsed.detail;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function registerTouristPanelDevice(
  workstationCode: string,
): Promise<TouristPanelDeviceStatus> {
  const token = getOrCreateTouristPanelDeviceToken();
  return publicFetch<TouristPanelDeviceStatus>("/api/v1/public/tourist-panel/devices/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      workstation_code: workstationCode,
      device_token: token,
      user_agent: navigator.userAgent.slice(0, 512),
    }),
  });
}

export async function fetchTouristPanelDeviceStatus(
  workstationCode: string,
): Promise<TouristPanelDeviceStatus> {
  return publicFetch<TouristPanelDeviceStatus>(
    `/api/v1/public/tourist-panel/devices/status?workstation_code=${encodeURIComponent(workstationCode)}`,
  );
}

export async function fetchTouristPanelDisplay(
  workstationCode: string,
): Promise<TouristPanelDisplay> {
  return publicFetch<TouristPanelDisplay>(
    `/api/v1/public/tourist-panel/display/${encodeURIComponent(workstationCode)}`,
  );
}

export async function updateTouristPanelSoldOut(
  workstationCode: string,
  payload: { sold_out_nights: string[]; sold_out_message?: string },
): Promise<TouristPanelSoldOut> {
  return publicFetch<TouristPanelSoldOut>(
    `/api/v1/public/tourist-panel/sold-out/${encodeURIComponent(workstationCode)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export async function updateTouristPanelAvailability(
  workstationCode: string,
  payload: TouristPanelAvailabilitySettingsUpdate,
): Promise<TouristPanelDisplay["availability"]> {
  return publicFetch<TouristPanelDisplay["availability"]>(
    `/api/v1/public/tourist-panel/availability/${encodeURIComponent(workstationCode)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchTouristPanelWorkstations(): Promise<TouristPanelWorkstation[]> {
  return api.get<TouristPanelWorkstation[]>("/api/v1/tourist-panel/workstations");
}

export async function createTouristPanelWorkstation(payload: {
  code: string;
  label: string;
  sort_order?: number;
}): Promise<TouristPanelWorkstation> {
  return api.post<TouristPanelWorkstation>("/api/v1/tourist-panel/workstations", payload);
}

export async function updateTouristPanelWorkstation(
  id: string,
  payload: { label?: string; sort_order?: number; is_active?: boolean },
): Promise<TouristPanelWorkstation> {
  return api.patch<TouristPanelWorkstation>(`/api/v1/tourist-panel/workstations/${id}`, payload);
}

export async function deactivateTouristPanelWorkstation(id: string): Promise<TouristPanelWorkstation> {
  return api.del<TouristPanelWorkstation>(`/api/v1/tourist-panel/workstations/${id}`);
}

export async function fetchTouristPanelContent(): Promise<TouristPanelContent> {
  return api.get<TouristPanelContent>("/api/v1/tourist-panel/content");
}

export async function updateTouristPanelContent(
  payload: Partial<TouristPanelContent>,
): Promise<TouristPanelContent> {
  return api.patch<TouristPanelContent>("/api/v1/tourist-panel/content", payload);
}

export async function approveTouristPanelDevice(deviceId: string): Promise<TouristPanelDevice> {
  return api.post<TouristPanelDevice>(`/api/v1/tourist-panel/devices/${deviceId}/approve`);
}

export async function revokeTouristPanelDevice(deviceId: string): Promise<TouristPanelDevice> {
  return api.post<TouristPanelDevice>(`/api/v1/tourist-panel/devices/${deviceId}/revoke`);
}

export async function updateTouristPanelDeviceLabel(
  deviceId: string,
  label: string | null,
): Promise<TouristPanelDevice> {
  return api.patch<TouristPanelDevice>(`/api/v1/tourist-panel/devices/${deviceId}`, { label });
}

export async function deleteTouristPanelDevice(deviceId: string): Promise<void> {
  await api.del(`/api/v1/tourist-panel/devices/${deviceId}`);
}
